import { useMembershipPlans } from '@/hooks/useMembershipPlans';
import type { MembershipPlanWithGateways } from '@/hooks/useMembershipPlans';
import { useBranding } from '@/hooks/useBranding';
import { useCurrentLocale } from '@/hooks/useCurrentLocale';
import { useMembershipPlanTranslations } from '@/hooks/useMembershipPlanTranslations';
import { useTrialEligibility } from '@/hooks/useTrialEligibility';
import { TRIBUTARIO_COMPARISON_DATA, TRIBUTARIO_PLAN_COLUMNS } from '@/constants/tributarioPlanData';
import { LEGACY_PLAN_DISPLAY_MAP } from '@/constants/membershipTiers';
import { TYPOGRAPHY } from '@/design-system';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Check, Star, Shield, Zap, Minus } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { LCommon } from '@/components/i18n';

// ─── Constants ───────────────────────────────────────────────────────────────

const COMPARISON_DATA = [
  { title: 'Verified profile badge', description: 'Verification badge on your professional profile', expert: true, professional: true, business: true },
  { title: 'Listed before non-verified', description: 'Appear above non-verified users in search results', expert: true, professional: true, business: true },
  { title: 'Daily updates', description: 'Receive tax alerts and news every day', expert: true, professional: true, business: true },
  { title: 'Horizontal featured card', description: 'Prominent wide card layout in search results', expert: false, professional: true, business: true },
  { title: 'Featured placement', description: 'Appear in promoted positions in search results', expert: false, professional: true, business: true },
  { title: 'Market insights', description: 'Exclusive market data and analytics', expert: false, professional: true, business: true },
  { title: 'Business profiles', description: 'Add company pages in up to 5 locations', expert: false, professional: false, business: true },
  { title: 'Exclusive content', description: 'Access to premium materials and analyses', expert: false, professional: false, business: true },
  { title: 'Priority publishing', description: 'Your publications are published with editorial priority', expert: false, professional: false, business: true },
  { title: 'Priority support', description: 'Priority assistance from the support team', expert: false, professional: false, business: true },
];

const PLAN_COLUMNS = [
  { key: 'expert' as const, label: 'Expert', subtitle: 'Get started' },
  { key: 'professional' as const, label: 'Professional', subtitle: 'More visibility' },
  { key: 'business' as const, label: 'Business', subtitle: 'Maximum reach' },
];


// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrice(plan: MembershipPlanWithGateways, isBrazil: boolean): string {
  const price = plan.billing_cycle === 'annual'
    ? (plan as Record<string, unknown>).price_annual as number | null
    : plan.price_monthly;
  if (!price || Number(price) === 0) return isBrazil ? 'Grátis' : 'Free';
  const currency = plan.currency?.toUpperCase() || 'USD';
  const symbol = currency === 'BRL' ? 'R$' : currency === 'EUR' ? '€' : '$';
  return `${symbol}${Math.round(Number(price))}`;
}

function getBillingLabel(cycle: string | null, isBrazil: boolean): string {
  if (!cycle) return '';
  if (cycle === 'monthly') return isBrazil ? '/mês' : '/month';
  if (cycle === 'annual' || cycle === 'yearly') return isBrazil ? '/ano' : '/year';
  return `/${cycle}`;
}

function getPlanIcon(index: number) {
  const icons = [Shield, Star, Zap];
  return icons[index % icons.length];
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function PlanComparisonTable() {
  return (
    <section className="mt-16 md:mt-24">
      <h2 className={`${TYPOGRAPHY.sectionTitleLarge} text-center mb-8`}>
        Compare plans & benefits
      </h2>
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="min-w-[200px]"><LCommon id="plan_selector.feature" defaultValue="Feature" /></TableHead>
              {PLAN_COLUMNS.map(col => (
                <TableHead key={col.key} className="text-center min-w-[120px]">
                  <div className="font-semibold">{col.label}</div>
                  <div className="text-xs font-normal text-muted-foreground">{col.subtitle}</div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {COMPARISON_DATA.map(row => (
              <TableRow key={row.title}>
                <TableCell>
                  <div className="font-medium">{row.title}</div>
                  <div className="text-sm text-muted-foreground">{row.description}</div>
                </TableCell>
                {PLAN_COLUMNS.map(col => (
                  <TableCell key={col.key} className="text-center">
                    {row[col.key] ? (
                      <Check className="h-5 w-5 text-accent mx-auto" />
                    ) : (
                      <Minus className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function TributarioComparisonTable() {
  return (
    <section className="mt-16 md:mt-24">
      <h2 className={`${TYPOGRAPHY.sectionTitleLarge} text-center mb-8`}>
        Compare planos e benefícios
      </h2>
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="min-w-[200px]"><LCommon id="plan_selector.feature_pt" defaultValue="Recurso" /></TableHead>
              {TRIBUTARIO_PLAN_COLUMNS.map(col => (
                <TableHead key={col.key} className="text-center min-w-[120px]">
                  <div className="font-semibold">{col.label}</div>
                  <div className="text-xs font-normal text-muted-foreground">{col.subtitle}</div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {TRIBUTARIO_COMPARISON_DATA.map(row => (
              <TableRow key={row.title}>
                <TableCell>
                  <div className="font-medium">{row.title}</div>
                  <div className="text-sm text-muted-foreground">{row.description}</div>
                </TableCell>
                {TRIBUTARIO_PLAN_COLUMNS.map(col => (
                  <TableCell key={col.key} className="text-center">
                    {row[col.key] ? (
                      <Check className="h-5 w-5 text-accent mx-auto" />
                    ) : (
                      <Minus className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

interface PlanSelectorProps {
  /** Called when user clicks Subscribe on a plan */
  onSelectPlan: (plan: MembershipPlanWithGateways, gatewayAccountId?: string) => void;
  /** Show the comparison table below the cards (default true) */
  showComparison?: boolean;
  /** Show the header with title + subtitle (default true) */
  showHeader?: boolean;
  /** If provided, enables contextual labels: Current Plan / Upgrade / Downgrade */
  currentPlanId?: string;
  /** Override location for plan filtering (e.g. publication's location_id). 33 = Brazil/BRL, else USD. */
  contentLocationId?: number | null;
  /** Price the user currently pays (for legacy plan display with grandfathered pricing) */
  currentPlanPrice?: number | null;
}

export function PlanSelector({
  onSelectPlan,
  showComparison = true,
  showHeader = true,
  currentPlanId,
  contentLocationId,
  currentPlanPrice,
}: PlanSelectorProps) {
  const { data: plans, isLoading } = useMembershipPlans();
  const { isBrazilBranding } = useBranding();
  const locale = useCurrentLocale();
  const isBrazilContent = contentLocationId === 33;
  const showPortuguese = isBrazilContent || isBrazilBranding || locale.startsWith('pt');
  const { getTranslation, isEnglish } = useMembershipPlanTranslations();
  const { data: trialData } = useTrialEligibility();
  const isTrialEligible = trialData?.isTrialEligible ?? true;
  const targetLocationId = isBrazilContent ? '33' : (isBrazilBranding ? '33' : '0');

  const activePlans = (plans ?? [])
    .filter(p => p.active)
    .filter(p => String(p.location_id) === targetLocationId)
    .filter(p => {
      const enabled = p.users_membership_plans_gateways?.filter(g => g.is_enabled) ?? [];
      const nonCourtesy = enabled.filter(
        g => g.payment_gateway_accounts != null
          && g.payment_gateway_accounts.provider !== 'courtesy'
      );
      if (enabled.length > 0 && nonCourtesy.length === 0) return false;
      return true;
    })
    .sort((a, b) => {
      const priceA = a.billing_cycle === 'annual'
        ? Number((a as Record<string, unknown>).price_annual ?? a.price_monthly ?? 0)
        : Number(a.price_monthly ?? 0);
      const priceB = b.billing_cycle === 'annual'
        ? Number((b as Record<string, unknown>).price_annual ?? b.price_monthly ?? 0)
        : Number(b.price_monthly ?? 0);
      return priceA - priceB;
    });

  // Contextual button label logic — resolve legacy plans to their active equivalent
  const legacyMappedId = currentPlanId ? LEGACY_PLAN_DISPLAY_MAP[currentPlanId] : undefined;
  const effectiveCurrentPlanId = currentPlanId && activePlans.find(p => p.id === currentPlanId)
    ? currentPlanId
    : legacyMappedId;
  const currentPlan = effectiveCurrentPlanId ? activePlans.find(p => p.id === effectiveCurrentPlanId) : null;
  const currentPrice = currentPlanPrice ?? currentPlan?.price_monthly ?? 0;
  const isLegacyPlan = !!legacyMappedId && effectiveCurrentPlanId === legacyMappedId;

  const getButtonLabel = (plan: MembershipPlanWithGateways): { label: string; isCurrentPlan: boolean } => {
    if (!currentPlanId) return { label: showPortuguese ? 'Assinar' : 'Subscribe', isCurrentPlan: false };
    if (plan.id === effectiveCurrentPlanId) return { label: showPortuguese ? 'Plano Atual' : 'Current Plan', isCurrentPlan: true };
    if ((plan.price_monthly ?? 0) > currentPrice) return { label: showPortuguese ? 'Fazer Upgrade' : 'Upgrade', isCurrentPlan: false };
    return { label: showPortuguese ? 'Fazer Downgrade' : 'Downgrade', isCurrentPlan: false };
  };

  return (
    <div>
      {showHeader && (
        <div className="text-center mb-12 md:mb-16">
          <h1 className={TYPOGRAPHY.pageTitle}>
            {showPortuguese ? 'Planos de Assinatura' : 'Membership Plans'}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            {showPortuguese
              ? 'Escolha o plano ideal para sua prática. Faça upgrade a qualquer momento para desbloquear mais recursos e visibilidade.'
              : 'Choose the right plan for your practice. Upgrade anytime to unlock more features and visibility.'}
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-3">
                <div className="h-6 bg-muted rounded w-2/3" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="h-10 bg-muted rounded w-1/3" />
                {[1, 2, 3].map(j => (
                  <div key={j} className="h-4 bg-muted rounded w-full" />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : activePlans.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-muted-foreground">
            {showPortuguese
              ? 'Nenhum plano disponível no momento.'
              : 'No plans available at this time.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activePlans.map((plan, index) => {
            const Icon = getPlanIcon(index);
            const isHighlighted = index === 1 && activePlans.length > 1;
            const enabledGateways = plan.users_membership_plans_gateways?.filter(g => g.is_enabled) || [];
            const translation = !isEnglish ? getTranslation(plan.id) : undefined;
            const planName = translation?.translatedName ?? plan.name;
            const planBullets = translation?.translatedBody ?? ((plan as Record<string, unknown>).description as string[] | undefined);
            const isThisLegacyCurrent = isLegacyPlan && plan.id === effectiveCurrentPlanId;

            return (
              <Card
                key={plan.id}
                className={`relative flex flex-col transition-shadow hover:shadow-lg ${
                  isHighlighted ? 'border-accent shadow-md ring-1 ring-accent/20' : ''
                }`}
              >
                {isHighlighted && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground">
                    {showPortuguese ? 'Mais Popular' : 'Most Popular'}
                  </Badge>
                )}
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="h-5 w-5 text-accent" />
                    <CardTitle className="text-xl">{planName}</CardTitle>
                  </div>
                  <CardDescription>
                    {plan.billing_cycle === 'annual'
                      ? (showPortuguese ? 'Cobrado anualmente' : 'Billed annually')
                      : (showPortuguese ? 'Cobrado mensalmente' : 'Billed monthly')}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-1">
                  <div className="mb-6">
                    {isTrialEligible && (plan as Record<string, unknown>).trial_days && Number((plan as Record<string, unknown>).trial_days) > 0 ? (
                      <>
                        <span className="text-4xl font-bold tracking-tight text-foreground">
                          {showPortuguese ? 'Teste Grátis' : 'Free Trial'}
                        </span>
                        <div className="text-sm text-muted-foreground mt-1">
                          {showPortuguese
                            ? `${(plan as Record<string, unknown>).trial_days} dias, depois ${formatPrice(plan, showPortuguese)}${getBillingLabel(plan.billing_cycle, showPortuguese)}`
                            : `${(plan as Record<string, unknown>).trial_days} days, then ${formatPrice(plan, showPortuguese)}${getBillingLabel(plan.billing_cycle, showPortuguese)}`}
                        </div>
                      </>
                    ) : isThisLegacyCurrent && currentPlanPrice != null && currentPlanPrice !== plan.price_monthly ? (
                      <>
                        <span className="text-4xl font-bold tracking-tight text-foreground">
                          R${Math.round(currentPlanPrice)}
                        </span>
                        <span className="text-muted-foreground ml-1">
                          {getBillingLabel(plan.billing_cycle, showPortuguese)}
                        </span>
                        <div className="text-sm text-muted-foreground mt-1">
                          <span className="line-through">R${Math.round(Number(plan.price_monthly ?? 0))}</span>
                          <span className="ml-2">{showPortuguese ? 'Preço especial legado' : 'Legacy pricing'}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold tracking-tight text-foreground">
                          {formatPrice(plan, showPortuguese)}
                        </span>
                        <span className="text-muted-foreground ml-1">
                          {getBillingLabel(plan.billing_cycle, showPortuguese)}
                        </span>
                      </>
                    )}
                  </div>

                  <Separator className="mb-5" />

                  <ul className="space-y-3">
                    {planBullets?.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                        <span className="text-muted-foreground">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="pt-4">
                  <div className="flex flex-col gap-2 w-full items-center">
                    {(() => {
                      const { label: btnLabel, isCurrentPlan } = getButtonLabel(plan);

                      if (isCurrentPlan) {
                        return (
                          <Button className="w-full" variant="outline" size="lg" disabled>
                            {btnLabel}
                          </Button>
                        );
                      }

                      const purchasable = enabledGateways.filter(
                        g => g.payment_gateway_accounts != null
                          && g.payment_gateway_accounts.provider !== 'courtesy'
                          && g.payment_gateway_accounts.status === 'active'
                          && g.external_price_id != null
                      );
                      if (purchasable.length === 0) {
                        return (
                          <Button className="w-full" variant="outline" size="lg" disabled>
                            {showPortuguese ? 'Em breve' : 'Coming Soon'}
                          </Button>
                        );
                      }
                      const firstGateway = purchasable[0];
                      const methodLabels = purchasable.map(g =>
                        g.payment_gateway_accounts?.provider === 'stripe'
                        ? (showPortuguese ? 'Cartão de Crédito' : 'Credit Card')
                        : 'PayPal'
                      );
                      return (
                        <>
                          <Button
                            className="w-full"
                            variant={isHighlighted && !currentPlanId ? 'default' : 'outline'}
                            size="lg"
                            onClick={() => onSelectPlan(plan, purchasable.length === 1 ? firstGateway.gateway_account_id : undefined)}
                          >
                            {btnLabel}
                          </Button>
                          {methodLabels.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {methodLabels.join(' · ')}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}

      {showComparison && !showPortuguese && <PlanComparisonTable />}
      {showComparison && showPortuguese && <TributarioComparisonTable />}
    </div>
  );
}
